import {debounce} from '@utils/debounce';
import {
  ISearchFilter,
  renderFilters,
} from '../Partials/SearchFilters/Scripts/search-filters';

/* Interfaces */
interface ISearchHit {
  id: string;
  heading: string;
  url: string;
  preamble: string;
  lastUpdatedDate: string;
  lastUpdatedDateISO: string;
  hitType: string;
  isBestBet: boolean;
  imageTag: string;
  categories: ICategory[];
  courseType: string;
  coursePrice: string;
  courseMemberPrice: string;
  courseScope: string;
  publishedDateIsoFormat: string;
  publishedDate: string;
}

interface ISearchResult {
  searchHits: ISearchHit[];
  searchFilters: ISearchFilter[];
  searchHtml: string;
  totalHits: number;
}

export interface ITextContent {
  bestBetTag: string;
  noResults: string;
  resultFor: string;
  resultsFor: string;
  updated: string;
  showMore: string;
  showing: string;
  outOf: string;
  noQuery: string;
  filterPlaceHolder: string;
  courseExcludingVat: string;
  courseMemberPrice: string;
  courseReadMoreAndBook: string;
  courseRegularPrice: string;
}

interface ICategory {
  key: number;
  value: string;
}

export const useSearch = () => {
  /* HTML elements */
  const searchContainer = document.querySelector('.search') as HTMLElement;
  const searchForm = document.querySelector('.search-field__form') as HTMLFormElement;
  const searchInput = document.querySelector('.search-field__input') as HTMLInputElement;
  const searchResultSection = document.querySelector('.search__result') as HTMLElement;
  const searchResultInfo = document.querySelector('.search__result-info') as HTMLParagraphElement;
  const searchRenderContainerNoResults = document.querySelector<HTMLElement>('.search__render-container--no-results');
  const translations = JSON.parse((document.querySelector('#text-translations') as HTMLScriptElement).textContent as string);

  /* Data */
  const {resultsToShow, currentLanguage} = searchForm.dataset;
  const pageSize = parseInt(resultsToShow!, 10);
  const isCourseListing = searchContainer.dataset.listingPageType === 'CourseListing';
  let currentPage: number = 0;
  const shouldHideDate = ((searchForm.dataset.hideDate || '').toLowerCase() === 'true');

  /* Text content (translated text) */
  const textContent: ITextContent = {
    noResults: translations.noResults as string,
    resultFor: translations.resultFor as string,
    resultsFor: translations.resultsFor as string,
    updated: translations.updated as string,
    showMore: translations.showMore as string,
    showing: translations.showing as string,
    outOf: translations.outOf as string,
    bestBetTag: translations.bestBetTag as string,
    noQuery: translations.noQuery as string,
    filterPlaceHolder: translations.filterPlaceHolder as string,
    courseExcludingVat: translations.courseExcludingVat as string,
    courseMemberPrice: translations.courseMemberPrice as string,
    courseReadMoreAndBook: translations.courseReadMoreAndBook as string,
    courseRegularPrice: translations.courseRegularPrice as string,
  };

  /* Global variables */
  let query: string = '';
  let apiSkipAmount = 0 as number;
  let apiTakeAmount = pageSize as number;
  const pageId = searchForm.dataset.pageid as string;
  let currentSort: string | null = null;
  let selectedFilterIds: number[] = [];
  let latestSearchRequestId = 0;
  let pageAdjusted = false;
  const paginationState = {
    mediaQuery: window.matchMedia('(max-width: 575.98px)') as MediaQueryList,
    lastTotalHits: 0 as number,
    lastAfterEl: null as HTMLElement | null,
    listenerAttached: false as boolean,
  };

  const getInitialSort = (): string | null => {
    // 1) URL param has priority
    const sortFromParam = new URLSearchParams(window.location.search).get("sort");
    if (sortFromParam) return sortFromParam;

    // 2) Read currently selected value (if any)
    const btn = document.querySelector<HTMLButtonElement>(".sort-options__button");
    if (btn?.dataset.value) return btn.dataset.value;

    // 3) Fallback: first option in the dropdown list
    const first = document.querySelector<HTMLButtonElement>(".sort-options__option-button");
    return first?.dataset.value ?? null;
  };

  const syncFacetInUrl = () => {
    const url = new URL(window.location.href);

    if (selectedFilterIds.length) {
      url.searchParams.set("facets", selectedFilterIds.join(",")); // browser encodes comma
    } else {
      url.searchParams.delete("facets");
    }

    // keep other params (q, sort, take) intact; ensure plain commas
    const newHref = url.toString().replace(/%2C/g, ",");
    window.history.replaceState({}, "", newHref);
  };

  const getCurrentTake = (): number => {
    // 1) If URL has ?take=..., prefer that
    const takeFromParam = new URLSearchParams(window.location.search).get('take');
    if (takeFromParam && !isNaN(parseInt(takeFromParam, 10))) {
      return parseInt(takeFromParam, 10);
    }

    // 2) Otherwise, count DOM items
    const currentCount = document.querySelectorAll('.search__result-list > li').length;
    if (currentCount > 0) return currentCount;

    // 3) Fallback to initial
    return parseInt(resultsToShow!, 10);
  };

  const getCurrentPageFromUrl = (): number => {
    const p = new URLSearchParams(window.location.search).get('page');
    if (!p) return 0;
    const n = parseInt(p, 10);
    return Number.isFinite(n) && n >= 1 ? n - 1 : 0;
  };

  const updateSkipForPage = () => {
    apiSkipAmount = currentPage * getCurrentTake();
  };

  /* Functions – Reset and clear DOM */
  const resetSearchResult = (): void => {
    renderWithoutResult();
  };

  const clearResultList = (): void => {
    const resultList: HTMLUListElement | null = document.querySelector('.search__result-list');
    if (resultList) {
      resultList.remove();
    }
  };

  const removePagination = (): void => {
    const nav = document.querySelector('nav.search__pagination') as HTMLElement | null;
    if (nav) {
      nav.remove();
    }
  };

  /* Functions – Get data from API */
  const getSearchResult = async (): Promise<ISearchResult | void> => {
    try {
      const url = `/api/search`;
      let params = `${url}?pageid=${pageId}&query=${query}&take=${apiTakeAmount}&skip=${apiSkipAmount}&language=${currentLanguage}`;

      if (selectedFilterIds.length) {
        params += `&facets=${selectedFilterIds.join(",")}`;
      }

      if (currentSort) {
        params += `&sort=${encodeURIComponent(currentSort)}`;
      }

      const response = await fetch(params, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return result;
    } catch (error) {
      console.error(error);
    }
  };

  /* Functions – Render DOM */
  const buildResultInfoHtml = (skip: number, shownCount: number, total: number): string => {
    if (total === 0) {
      return `${textContent.showing} <strong>0-0</strong> ${textContent.outOf} <strong>0</strong>`;
    }
    const start = skip + 1;
    const end = skip + shownCount;
    let html = "";
    if (textContent.showing) {
      html += `${textContent.showing} <strong>${start}-${end}</strong> `;
    }
    if (textContent.resultsFor) {
      html += `${textContent.resultsFor} `;
    }
    if (textContent.outOf) {
      html += `${textContent.outOf} <strong>${total}</strong>`;
    }
    return html;
  };

  const getCourseTypeIconClass = (courseType: string): string => {
    const normalizedCourseType = courseType.toLocaleLowerCase(currentLanguage);

    if (normalizedCourseType.includes('online')) {
      return 'icon--computer-play';
    }

    if (normalizedCourseType.includes('webinar') || normalizedCourseType.includes('webbseminarium')) {
      return 'icon--computer-live';
    }

    if (normalizedCourseType.includes('hybrid') ||
      normalizedCourseType.includes('klassrum') ||
      normalizedCourseType.includes('på plats') ||
      normalizedCourseType.includes('on-site')) {
      return 'icon--on-site';
    }

    if (normalizedCourseType.includes('distans') || normalizedCourseType.includes('distance')) {
      return 'icon--computer-distance';
    }

    if (normalizedCourseType.includes('kursprogram') || normalizedCourseType.includes('course program')) {
      return 'icon--books';
    }

    return 'icon--calendar';
  };

  const renderResultItems = (searchHits: ISearchHit[], resultList: HTMLUListElement): void => {
    searchHits.forEach((item, index) => {
      const resultItem = document.createElement('li') as HTMLLIElement;
      resultItem.classList.add('search__result-item', 'site-page-partial');

      if (isCourseListing) {
        resultItem.classList.add('site-page-partial--accordion');
      }

      const articleContent = document.createElement('article') as HTMLElement;
      articleContent.classList.add('site-page-partial__content', 'site-page-partial__content--listing');
      resultItem.appendChild(articleContent);

      if (item.imageTag) {
        const figure = document.createElement('figure') as HTMLElement;
        figure.classList.add('site-page-partial__image-container', 'site-page-partial__image-container--listing');
        articleContent.appendChild(figure);
        figure.innerHTML = item.imageTag;
      }

      const textContainer = document.createElement('div') as HTMLDivElement;
      textContainer.classList.add('site-page-partial__text-container', 'site-page-partial__text-container--listing');
      articleContent.appendChild(textContainer);

      const textContentContainer = document.createElement('div') as HTMLDivElement;
      textContentContainer.classList.add('site-page-partial__text-content');
      textContainer.appendChild(textContentContainer);

      const dateTimeCategories = document.createElement('div') as HTMLDivElement;
      dateTimeCategories.classList.add('site-page-partial__date-time-and-categories');
      textContentContainer.appendChild(dateTimeCategories);

      if (isCourseListing) {
        dateTimeCategories.classList.add('site-page-partial__course-metadata');

        const courseMetadataList = document.createElement('ul') as HTMLUListElement;
        courseMetadataList.classList.add('site-page-partial__course-metadata-list');
        dateTimeCategories.appendChild(courseMetadataList);

        const createCourseMetadataItem = (iconClass: string): HTMLLIElement => {
          const metadataItem = document.createElement('li') as HTMLLIElement;
          metadataItem.classList.add('site-page-partial__course-metadata-item');
          courseMetadataList.appendChild(metadataItem);

          const metadataIcon = document.createElement('span') as HTMLSpanElement;
          metadataIcon.classList.add('site-page-partial__course-metadata-icon', 'icon', iconClass);
          metadataIcon.setAttribute('aria-hidden', 'true');
          metadataItem.appendChild(metadataIcon);

          return metadataItem;
        };

        if (item.courseType) {
          const courseTypeItem = createCourseMetadataItem(getCourseTypeIconClass(item.courseType));
          const courseTypeText = document.createElement('span') as HTMLSpanElement;
          courseTypeText.innerText = item.courseType;
          courseTypeItem.appendChild(courseTypeText);
        }

        if (item.courseScope) {
          const courseScopeItem = createCourseMetadataItem('icon--clock');
          const courseScopeText = document.createElement('span') as HTMLSpanElement;
          courseScopeText.innerText = item.courseScope;
          courseScopeItem.appendChild(courseScopeText);
        }

        if (item.coursePrice || item.courseMemberPrice) {
          const coursePriceItem = createCourseMetadataItem('icon--tag');
          const coursePriceText = document.createElement('span') as HTMLSpanElement;
          coursePriceItem.appendChild(coursePriceText);

          if (item.coursePrice) {
            coursePriceText.append(`${textContent.courseRegularPrice}: ${item.coursePrice}`);
          }

          if (item.coursePrice && item.courseMemberPrice) {
            coursePriceText.append(' / ');
          }

          if (item.courseMemberPrice) {
            coursePriceText.append(`${textContent.courseMemberPrice}: ${item.courseMemberPrice}`);
          }

          const excludingVat = document.createElement('span') as HTMLSpanElement;
          excludingVat.classList.add('site-page-partial__course-metadata-tax');
          excludingVat.innerText = ` ${textContent.courseExcludingVat}`;
          coursePriceText.appendChild(excludingVat);
        }
      } else {
        if (item.categories && item.categories.length > 0) {
          const categoryList = document.createElement('ul') as HTMLUListElement;
          categoryList.classList.add('site-page-partial__category-list', 'tag-container');
          dateTimeCategories.appendChild(categoryList);

          item.categories.forEach((cat: ICategory) => {
            const category = document.createElement('li') as HTMLLIElement;
            category.classList.add('site-page-partial__category-item', 'tag', 'tag--small');
            category.textContent = cat.value;
            categoryList.appendChild(category);
          });
        }

        if (!shouldHideDate) {
          const date = document.createElement('time') as HTMLTimeElement;
          date.classList.add('site-page-partial__date-time');
          date.dateTime = item.publishedDateIsoFormat;
          date.textContent = item.publishedDateIsoFormat;
          dateTimeCategories.appendChild(date);
        } else {
          dateTimeCategories.classList.add('site-page-partial__date-time-and-categories--no-date');
        }
      }

      const heading = document.createElement('h3') as HTMLHeadingElement;
      heading.classList.add('site-page-partial__heading', 'heading');
      textContentContainer.appendChild(heading);

      if (isCourseListing) {
        if (item.preamble) {
          const accordionPanelId = `course-preamble-${item.id || `${currentPage}-${index}`}`;
          const accordionToggle = document.createElement('button') as HTMLButtonElement;
          accordionToggle.classList.add('site-page-partial__accordion-toggle');
          accordionToggle.type = 'button';
          accordionToggle.setAttribute('aria-expanded', 'false');
          accordionToggle.setAttribute('aria-controls', accordionPanelId);
          heading.appendChild(accordionToggle);

          const accordionTitle = document.createElement('span') as HTMLSpanElement;
          accordionTitle.classList.add('site-page-partial__accordion-title');
          accordionTitle.innerText = item.heading;
          accordionToggle.appendChild(accordionTitle);

          const accordionIcon = document.createElement('span') as HTMLSpanElement;
          accordionIcon.classList.add('site-page-partial__accordion-icon', 'icon', 'icon--chevron-down');
          accordionIcon.setAttribute('aria-hidden', 'true');
          accordionToggle.appendChild(accordionIcon);

          const preamble = document.createElement('p') as HTMLParagraphElement;
          preamble.id = accordionPanelId;
          preamble.classList.add('site-page-partial__preamble', 'site-page-partial__accordion-panel');
          preamble.innerHTML = item.preamble;
          preamble.hidden = true;
          textContentContainer.appendChild(preamble);

          accordionToggle.addEventListener('click', () => {
            const isExpanded = accordionToggle.getAttribute('aria-expanded') === 'true';
            accordionToggle.setAttribute('aria-expanded', String(!isExpanded));
            preamble.hidden = isExpanded;
          });
        } else {
          heading.innerText = item.heading;
        }

        const courseLink = document.createElement('a') as HTMLAnchorElement;
        courseLink.classList.add('site-page-partial__accordion-link', 'button');
        courseLink.href = item.url;
        courseLink.innerText = textContent.courseReadMoreAndBook;
        courseLink.setAttribute('aria-label', `${textContent.courseReadMoreAndBook}: ${item.heading}`);
        textContainer.appendChild(courseLink);

        resultList.appendChild(resultItem);
        return;
      }

      const link = document.createElement('a') as HTMLAnchorElement;
      link.classList.add('site-page-partial__link');
      link.setAttribute('href', item.url);
      link.innerText = item.heading;
      heading.appendChild(link);

      if (item.preamble) {
        const preamble = document.createElement('p') as HTMLParagraphElement;
        preamble.classList.add('site-page-partial__preamble');
        preamble.innerHTML = item.preamble;
        textContentContainer.appendChild(preamble);
      }

      const arrowIconContainer = document.createElement('div') as HTMLDivElement;
      arrowIconContainer.classList.add('site-page-partial__arrow-icon-container');
      textContainer.appendChild(arrowIconContainer);

      const arrowIcon = document.createElement('span') as HTMLSpanElement;
      arrowIcon.classList.add('site-page-partial__arrow-icon', 'icon', 'icon--arrow-right');
      arrowIconContainer.appendChild(arrowIcon);

      expandLinkToContainer(articleContent, link);

      resultList.appendChild(resultItem);
    });
  };

  const renderPagination = (totalHits: number, afterEl: HTMLElement): void => {
    paginationState.lastTotalHits = totalHits;
    paginationState.lastAfterEl = afterEl;

    if (!paginationState.listenerAttached) {
      const mediaQueryHandler = () => {
        if (!paginationState.lastAfterEl) {
          return;
        }
        renderPagination(paginationState.lastTotalHits, paginationState.lastAfterEl);
      };

      if (paginationState.mediaQuery.addEventListener) {
        paginationState.mediaQuery.addEventListener('change', mediaQueryHandler);
      } else if ('onchange' in paginationState.mediaQuery) {
        (paginationState.mediaQuery as MediaQueryList & { onchange: ((e: MediaQueryListEvent) => void) | null }).onchange = mediaQueryHandler;
      } else {
        window.addEventListener('resize', mediaQueryHandler);
      }

      paginationState.listenerAttached = true;
    }

    const numberOfPages = Math.ceil(totalHits / pageSize);
    if (numberOfPages <= 1) {
      removePagination();
      return;
    }

    let nav = document.querySelector('nav.search__pagination') as HTMLElement | null;
    let ul: HTMLUListElement;

    if (!nav) {
      nav = document.createElement('nav');
      nav.className = 'search__pagination';
      nav.setAttribute('aria-label', 'Pagination');
      ul = document.createElement('ul');
      ul.className = 'pagination';
      nav.appendChild(ul);
    } else {
      ul = nav.querySelector('ul.pagination') as HTMLUListElement | null ?? document.createElement('ul');
      if (!ul.parentElement) {
        ul.className = 'pagination';
        nav.appendChild(ul);
      }
      ul.innerHTML = '';
    }

    const buildUrl = (pageIndex: number): string => {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(pageIndex + 1));
      return url.toString();
    };

    const append = (el: HTMLElement) => ul.appendChild(el);

    const createControl = (isPrev: boolean): void => {
      const disabled = isPrev ? currentPage === 0 : currentPage >= numberOfPages - 1;
      const target = isPrev ? Math.max(0, currentPage - 1) : Math.min(numberOfPages - 1, currentPage + 1);
      const li = document.createElement('li');
      li.className = `pagination__item pagination__item--control ${isPrev ? 'pagination__item--previous' : 'pagination__item--next'}${disabled ? ' pagination__item--disabled' : ''}`;

      const chevronIcon = document.createElement('span');
      chevronIcon.classList.add('pagination__icon', 'icon', isPrev ? 'icon--chevron-left' : 'icon--chevron-right');
      chevronIcon.setAttribute('aria-hidden', 'true');

      if (disabled) {
        const span = document.createElement('span');
        span.className = 'pagination__label';
        span.setAttribute('aria-disabled', 'true');
        span.appendChild(chevronIcon);
        li.appendChild(span);
      } else {
        const a = document.createElement('a');
        a.className = 'pagination__link';
        a.href = buildUrl(target);
        a.setAttribute('aria-label', isPrev ? 'Previous page' : 'Next page');
        a.appendChild(chevronIcon);
        a.addEventListener('click', (e) => {
          e.preventDefault();
          currentPage = target;
          updateSkipForPage();
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('page', String(currentPage + 1));
          window.history.replaceState({}, '', newUrl.toString());
          search({preserveTake: true, keepPage: true});
          window.scrollTo({
            top: searchResultSection.offsetTop - 80,
            behavior: 'smooth',
          });
        });
        li.appendChild(a);
      }
      append(li);
    };

    const createNumberPage = (idx: number): void => {
      const li = document.createElement('li');
      li.className = 'pagination__item pagination__item--number' + (idx === currentPage ? ' pagination__item--active' : '');
      const num = idx + 1;
      if (idx === currentPage) {
        const span = document.createElement('span');
        span.className = 'pagination__label';
        span.textContent = String(num);
        span.setAttribute('aria-current', 'page');
        li.appendChild(span);
      } else {
        const a = document.createElement('a');
        a.className = 'pagination__link';
        a.textContent = String(num);
        a.href = buildUrl(idx);
        a.setAttribute('aria-label', `Go to page ${num}`);
        a.addEventListener('click', (e) => {
          e.preventDefault();
          currentPage = idx;
          updateSkipForPage();
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('page', String(currentPage + 1));
          window.history.replaceState({}, '', newUrl.toString());
          search({preserveTake: true, keepPage: true});
          window.scrollTo({
            top: searchResultSection.offsetTop - 80,
            behavior: 'smooth',
          });
        });
        li.appendChild(a);
      }
      append(li);
    };

    const createEllipsis = (): void => {
      const li = document.createElement('li');
      li.className = 'pagination__item pagination__item--ellipsis';
      const span = document.createElement('span');
      span.className = 'pagination__ellipsis';
      span.textContent = '…';
      span.setAttribute('aria-hidden', 'true');
      li.appendChild(span);
      append(li);
    };

    const isMobilePagination: boolean = paginationState.mediaQuery.matches;

    if (isMobilePagination) {
      // Simplified pagination on mobile
      createControl(true);
      let start: number = Math.max(0, currentPage - 1);
      let end: number = start + 2;

      if (end > numberOfPages - 1) {
        end = numberOfPages - 1;
        start = Math.max(0, end - 2);
      }

      for (let p: number = start; p <= end; p++) {
        createNumberPage(p);
      }

      createControl(false);
    } else {
      // Desktop pagination window calculation
      const windowSize: number = 3;
      let start = currentPage - Math.floor(windowSize / 2);

      if (start < 0) {
        start = 0;
      }

      let end = start + windowSize - 1;

      if (end > numberOfPages - 1) {
        end = numberOfPages - 1;
      }

      start = Math.max(0, end - windowSize + 1);
      const lastIndex: number = numberOfPages - 1;
      createControl(true);

      // Show the first page and leading ellipsis if needed
      if (start > 0) {
        createNumberPage(0);
        if (start > 1) {
          createEllipsis();
        }
      }

      for (let p: number = start; p <= end; p++) {
        createNumberPage(p);
      }

      // Show trailing ellipsis and the last page if needed
      if (end < lastIndex) {
        if (end < lastIndex - 1) {
          createEllipsis();
        }
        createNumberPage(lastIndex);
      }

      createControl(false);
    }

    afterEl.insertAdjacentElement('afterend', nav);
  };

  const renderResult = (searchResult: ISearchResult): void => {
    clearResultList();
    const hasNoResults = searchResult.totalHits === 0;
    if (searchRenderContainerNoResults) {
      searchRenderContainerNoResults.hidden = !hasNoResults;
      searchResultSection.classList.toggle('search__result--empty', hasNoResults);
    }
    if (searchResult.searchHtml) {
      searchResultSection.innerHTML = searchResult.searchHtml;
      return;
    }
    // Result info
    if (searchResultInfo) {
      searchResultInfo.innerHTML = buildResultInfoHtml(apiSkipAmount, searchResult.searchHits.length, searchResult.totalHits);
    }
    if (searchResult.totalHits > 0) {
      const resultList = document.createElement('ul') as HTMLUListElement;
      resultList.classList.add('search__result-list');

      if (isCourseListing) {
        resultList.classList.add('search__result-list--accordion');
      }

      searchResultSection.appendChild(resultList);
      renderResultItems(searchResult.searchHits, resultList);
      renderPagination(searchResult.totalHits, resultList);
    }
  };

  const renderWithoutResult = (): void => {
    clearResultList();

    if (!searchInput.value) {
      if (searchResultInfo) {
        searchResultInfo.textContent = textContent.noQuery;
      }
    } else {
      searchInput.value = '';

      if (searchResultInfo) {
        searchResultInfo.textContent = '';
      }
    }
  };

  /* Functions - Search */
  const search = async (opts: {
    sort?: string,
    preserveTake?: boolean,
    keepPage?: boolean,
    preserveFilters?: boolean
  } = {}): Promise<void> => {
    const requestId = ++latestSearchRequestId;
    const {sort = null, preserveTake = true, keepPage = false, preserveFilters = false} = opts;
    if (sort) {
      currentSort = sort;
    }
    if (!keepPage) {
      currentPage = 0;
      pageAdjusted = false;
    }
    updateSkipForPage();
    removePagination();

    if (preserveTake) {
      apiTakeAmount = getCurrentTake();
    } else {
      apiTakeAmount = parseInt(resultsToShow!, 10);
    }

    toggleLoading(true);

    const searchResult = await getSearchResult();

    if (requestId !== latestSearchRequestId) {
      return;
    }

    if (!searchResult) {
      toggleLoading(false);
      return;
    }

    const totalPages = Math.ceil(searchResult.totalHits / pageSize);
    if (currentPage >= totalPages && totalPages > 0 && !pageAdjusted) {
      currentPage = totalPages - 1;
      pageAdjusted = true;
      updateSkipForPage();
      search({preserveTake: true, keepPage: true, preserveFilters});
      return;
    }
    pageAdjusted = false;
    renderResult(searchResult);

    if (searchResult.searchFilters && !preserveFilters) {
      renderFilters(searchResult.searchFilters, translations, {
        container: document.querySelector<HTMLElement>(".search__filter-container"),
        preselectedIds: selectedFilterIds,

        onChange: () => {
          const hasRenderedFilters = Boolean(document.querySelector('.search__filter-container .filter-dropdown'));
          selectedFilterIds = Array.from(
            document.querySelectorAll<HTMLInputElement>('.filter-dropdown__option-checkbox[data-option-id]:not([value=""])'))
            .filter(c => c.checked)
            .map(c => Number(c.dataset.optionId))
            .filter(n => Number.isFinite(n));

          syncFacetInUrl();
          search({ preserveTake: false, preserveFilters: hasRenderedFilters });
        },
      });
    }

    // Keep ?take=... in the URL in sync
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(currentPage + 1));
    url.searchParams.set('take', String(apiTakeAmount));
    if (currentSort) url.searchParams.set('sort', currentSort);
    window.history.replaceState({}, '', url);

    toggleLoading(false);
  };

  const searchFromQueryString = (): void => {
    const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
    const queryFromParam: string | null = urlParams.get('q');
    const takeFromParam: string | null = urlParams.get('take');
    const facetFromParam: string | null = urlParams.get('facets');
    const sortFromParam = urlParams.get('sort');
    currentPage = getCurrentPageFromUrl();

    query = queryFromParam;

    if (query) {
      query = query.trim();
      searchInput.value = query;
    } else {
      query = '';
    }

    if (takeFromParam && !isNaN(parseInt(takeFromParam, 10))) {
      apiTakeAmount = parseInt(takeFromParam, 10);
    }

    if (facetFromParam) {
      selectedFilterIds = facetFromParam
        .split(",")
        .map(s => Number(s))
        .filter(n => Number.isFinite(n));
    }

    if (sortFromParam) {
      currentSort = sortFromParam;
    }

    Promise.all([search({ preserveTake: true, keepPage: true })]).then(() => {
      toggleResultInfo(true);
      toggleFacets(true);
    });
  };

  const resetSearchQuery = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    window.history.replaceState({}, '', url);
    query = '';
    Promise.all([search()]).then(() => {
      toggleResultInfo(true);
      toggleFacets(true);
    });
  };

  const toggleLoading = (on: boolean): void => {
    searchResultSection.classList.toggle('search__result--loading', on);
  };

  const toggleFacets = (isVisible: boolean): void => {
    const facets = document.querySelector('.search__facets') as HTMLDivElement;
    facets?.classList.toggle('search__facets--visible', isVisible);
  };

  const toggleResultInfo = (isVisible: boolean): void => {
    const resultInfo = document.querySelector('.search__result-info') as HTMLDivElement;
    resultInfo?.classList.toggle('search__result-info--visible', isVisible);
  };

  const shouldRunInitialSearch = (): boolean => {
    // Look at the data attribute on your <form>
    // e.g. <form class="search-field__form" data-run-default="true">
    const v = (searchForm.dataset.runDefault || "").toLowerCase();

    // Accept "true", "1", or "yes" as truthy
    return v === "true" || v === "1" || v === "yes";
  };

  /* Event listeners */

  // Event that happens if someone enters url with query string
  const init = () => {
    if (window.location.search) {
      searchFromQueryString();
    } else if (shouldRunInitialSearch()) {
      currentSort = getInitialSort();
      search({ sort: currentSort || undefined });
    }
    searchInput?.focus();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Event that happens when users browse through their browser history, e.g. use the back button.
  window.addEventListener('popstate', (): void => {
    if (window.location.search) {
      currentPage = getCurrentPageFromUrl();
      updateSkipForPage();
      searchFromQueryString();
    } else {
      resetSearchResult();
    }
    searchInput.focus();
  });

  // Event that happens when user submits a search with the form
  searchForm.addEventListener('submit', (event: SubmitEvent): void => {
    event.preventDefault();

    const relevance = "Relevance";

    query = searchInput.value.trim();
    currentPage = 0;
    updateSkipForPage();

    // Build URL with both q and sort=relevance
    const url = new URL(window.location.href);
    if (query) {
      url.searchParams.set('q', query);
      url.searchParams.set('sort', relevance);
      url.searchParams.set('page', '1');
    } else {
      url.searchParams.delete('q');
      url.searchParams.delete('sort');
      url.searchParams.delete('page');
    }
    window.history.pushState({}, '', url);

    if (query.length > 0) {
      // Update Sort dropdown UI (if present) without triggering a second search
      window.SortOptions?.set(relevance, false);

      // Keep search.ts in sync and run search with explicit sort
      currentSort = relevance;
      Promise.all([search({ sort: relevance })]).then(() => {
        toggleResultInfo(true);
        toggleFacets(true);
      });
    } else {
      resetSearchResult();
    }
  });

  document.addEventListener(
    "sort:change",
    ((e: CustomEvent<{ value: string }>) => {
      const v = e.detail?.value;
      if (!v) return;

      // keep internal state in sync
      currentSort = v;
      currentPage = 0;
      updateSkipForPage();
      // run a fresh search (reset pagination to initial page)
      Promise.all([search({ sort: v, preserveTake: false })]).then(() => {
        toggleResultInfo(true);
        toggleFacets(true);
      });
    }) as EventListener);

  searchInput.addEventListener('input', () => {
    if (searchInput.value === '') {
      resetSearchQuery();
    }
  });

  // Expand link click target to the entire container
  const expandedContainers = new WeakSet<HTMLElement>();
  const expandLinkToContainer = (container: HTMLElement, link: HTMLAnchorElement) => {
    // If link has already been expanded to container then return
    if (expandedContainers.has(container)) return;

    const href = link.getAttribute('href');
    if (!href) return;

    container.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;

      // If the click is on an actual anchor element then return
      if (target?.closest('a')) return;

      if (e.metaKey || e.ctrlKey) {
        window.open(href, '_blank');
      } else {
        window.location.href = href;
      }
    });

    expandedContainers.add(container);
  };

  // Scroll event to fix the search input position when fixed
  window.addEventListener("scroll", debounce(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    const headerHeight = document.querySelector('.header')?.clientHeight || 0;

    if (scrollTop > headerHeight && !searchContainer.classList.contains('search--search-input-fixed')) {
      searchContainer.classList.add('search--search-input-fixed-loading');

      setTimeout(() => {
        // If the user has scrolled back to the top before the timeout completes, remove the class
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop || 0;

        if (currentScrollTop < headerHeight) {
          searchContainer.classList.remove('search--search-input-fixed-loading');
          searchContainer.classList.remove('search--search-input-fixed');
          return;
        }

        // Otherwise, add the fixed class
        searchContainer.classList.add('search--search-input-fixed');
        searchContainer.classList.remove('search--search-input-fixed-loading');
      }, 1000);
    } else if (scrollTop < headerHeight && searchContainer.classList.contains('search--search-input-fixed')) {
      searchContainer.classList.remove('search--search-input-fixed');
    }

  }, 100));

  return { search };
};

useSearch();
